import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X, Trash2 } from "lucide-react";

const ContactDeleteModal = ({
  isOpen,
  onClose,
  onConfirm,
  item,
  type = "contact",
  isDeleting = false,
}) => {
  if (!isOpen || !item) return null;

  const isGroup = type === "group";
  const itemName = isGroup
    ? item.group_name || item.name
    : item.name || item.phone_number;

  return (
    <AnimatePresence>
      <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="p-6 w-full max-w-md bg-white rounded-2xl"
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-3 items-center">
              <div className="flex justify-center items-center w-10 h-10 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                Delete {isGroup ? "Group" : "Contact"}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 rounded-lg transition-colors duration-200 hover:text-gray-600 hover:bg-gray-100"
              disabled={isDeleting}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="mb-6">
            <p className="mb-4 text-gray-600">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-gray-900">"{itemName}"</span>?
            </p>

            {isGroup && (
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex gap-3 items-start">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="mb-1 font-medium text-yellow-800">
                      Warning
                    </h4>
                    <p className="text-sm text-yellow-700">
                      This will permanently delete the group and all{" "}
                      {item.contact_count || 0} contacts within it. This action
                      cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!isGroup && (
              <p className="text-sm text-gray-500">
                This action cannot be undone.
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 font-medium text-gray-700 rounded-lg border border-gray-300 transition-colors duration-200 hover:bg-gray-50"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(item)}
              className="flex flex-1 gap-2 justify-center items-center px-4 py-3 font-medium text-white bg-red-600 rounded-lg transition-colors duration-200 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 rounded-full border-b-2 border-white animate-spin"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Delete {isGroup ? "Group" : "Contact"}
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ContactDeleteModal;
